import { useEffect, useRef } from 'react'

interface VideoTileProps {
  stream: MediaStream | null
  displayName: string
  muted?: boolean
  mirrored?: boolean
  audioOutputId?: string | null
  audioEnabled?: boolean
  videoEnabled?: boolean
  screen?: boolean
  connectionState?: RTCPeerConnectionState
  onClick?: () => void
  focused?: boolean
  pinned?: boolean
}

export function VideoTile({
  stream,
  displayName,
  muted,
  mirrored,
  audioOutputId,
  audioEnabled = true,
  videoEnabled = true,
  connectionState,
  onClick,
  focused,
  pinned,
  screen,
}: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.srcObject = stream
    if (stream) void el.play().catch(() => {})
  }, [stream])

  useEffect(() => {
    const el = videoRef.current
    if (!el || !audioOutputId) return
    const settable = el as HTMLVideoElement & { setSinkId?: (id: string) => Promise<void> }
    if (typeof settable.setSinkId === 'function') {
      void settable.setSinkId(audioOutputId).catch(() => {
        /* ignore — not supported in all browsers */
      })
    }
  }, [audioOutputId])

  const initials = displayName
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div
      className={`video-tile ${focused ? 'is-focused' : ''} ${pinned ? 'is-pinned' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        className={`video-el ${mirrored ? 'mirrored' : ''} ${!videoEnabled ? 'hidden' : ''}`}
      />
      {!videoEnabled ? (
        <div className="video-placeholder">
          <div className="avatar" aria-hidden>
            {initials || '?'}
          </div>
        </div>
      ) : null}
      {connectionState === 'failed' || connectionState === 'disconnected' ? (
        <div className="tile-conn-error">Verbindung getrennt</div>
      ) : null}
      <div className="video-overlay">
        <span className="name">{displayName}</span>
        <span className="badges">
          {!audioEnabled ? <span className="badge badge-mute">🔇</span> : null}
          {screen ? <span className="badge">📺</span> : null}
        </span>
      </div>
    </div>
  )
}
